/**
 * Datas do dado simulado de `operacao/financeiro`, sempre relativas a hoje.
 *
 * CLAUDE.md, exceções do Fechamento Mensal e do Ciclo mensal da SME: "Nenhuma
 * competência literal no mock". Ano-mês escrito à mão envelhece sozinho, e um
 * dia a tela mostraria à SMT meses que já passaram. O JSON guarda a distância
 * em meses até o mês corrente (`mesesAtras`) e, quando a data tem dia, o dia do
 * mês e a hora; o `dados.ts` de cada tela monta o texto com as funções daqui.
 * `dadosSimulados.spec.ts` (módulo `financeiro`) falha se alguém escrever um
 * ano-mês no JSON.
 *
 * TEMPORÁRIO — acompanha os mocks, sai junto com eles no desenvolvimento do
 * backend.
 */

/** Um dia dentro de um mês contado a partir do mês corrente (`0` = este mês). */
export interface DiaRelativo {
  mesesAtras: number;
  dia: number;
}

/** Um instante: o dia mais a hora local (`HH:MM:SS`). */
export interface InstanteRelativo extends DiaRelativo {
  hora: string;
}

/** Fuso de São Paulo, o das datas e horas que as telas exibem. */
const FUSO_DE_SAO_PAULO = '-03:00';

function doisDigitos(n: number): string {
  return String(n).padStart(2, '0');
}

function mesDeslocado(mesesAtras: number, hoje: Date): Date {
  return new Date(hoje.getFullYear(), hoje.getMonth() - mesesAtras, 1);
}

/** `AAAA-MM` deslocado `mesesAtras` meses a partir do mês de `hoje`. */
export function anoMesAtras(mesesAtras: number, hoje = new Date()): string {
  const mes = mesDeslocado(mesesAtras, hoje);
  return `${mes.getFullYear()}-${doisDigitos(mes.getMonth() + 1)}`;
}

/** `AAAA-MM-01` do mês deslocado. */
export function primeiroDiaDoMesAtras(mesesAtras: number, hoje = new Date()): string {
  return `${anoMesAtras(mesesAtras, hoje)}-01`;
}

/** `AAAA-MM-DD` do último dia do mês deslocado (28 a 31). */
export function ultimoDiaDoMesAtras(mesesAtras: number, hoje = new Date()): string {
  const base = mesDeslocado(mesesAtras, hoje);
  const ultimo = new Date(base.getFullYear(), base.getMonth() + 1, 0).getDate();
  return `${anoMesAtras(mesesAtras, hoje)}-${doisDigitos(ultimo)}`;
}

/**
 * `AAAA-MM-DD`. Dia que o mês não tem (31 num mês de 30) cai no último dia, em
 * vez de transbordar para o mês seguinte e embaralhar a ordem da lista.
 */
export function dataAtras({ mesesAtras, dia }: DiaRelativo, hoje = new Date()): string {
  const ultimo = Number(ultimoDiaDoMesAtras(mesesAtras, hoje).slice(-2));
  return `${anoMesAtras(mesesAtras, hoje)}-${doisDigitos(Math.min(dia, ultimo))}`;
}

/** ISO 8601 com o fuso de São Paulo: `AAAA-MM-DDTHH:MM:SS-03:00`. */
export function instanteAtras(instante: InstanteRelativo, hoje = new Date()): string {
  return `${dataAtras(instante, hoje)}T${instante.hora}${FUSO_DE_SAO_PAULO}`;
}
